/**
 * How a failed write is described to the user.
 *
 * This lives in the tx engine rather than in each screen because of what the
 * engine does to an error on its way out: `serializeError` (codec.js) flattens
 * it to `{ name, message, code }` before it is stored, so by the time a screen
 * reads `error` off `usePersistentWriteContract`, viem's error class, its
 * `cause` chain, its `shortMessage` and MetaMask's `4001` are all gone. A screen
 * testing for any of those sees nothing and reports a network failure — or a
 * crash — for a transaction the user simply declined in their wallet.
 *
 * Everything here is written to give the same answer for a live viem error and
 * for that flattened replay.
 */

/**
 * viem/wagmi surface user-rejection in several shapes depending on wallet.
 * Getting this wrong would mark a rejected prompt as a pending transaction and
 * leave a permanent ghost spinner after reload.
 *
 * The message sniff is the part that survives serialization: `code` sits on a
 * cause two levels down that is not copied, and `name` serializes as the outer
 * wrapper (`ContractFunctionExecutionError`), never `UserRejectedRequestError`.
 */
export function isUserRejection(err) {
  if (!err) return false;
  const code = err.code ?? err.cause?.code;
  if (code === 4001 || code === "ACTION_REJECTED") return true;
  const name = err.name || err.cause?.name || "";
  if (name === "UserRejectedRequestError") return true;
  const msg = `${err.shortMessage || ""} ${err.message || ""}`.toLowerCase();
  return msg.includes("user rejected") || msg.includes("user denied") || msg.includes("rejected the request");
}

/**
 * The sentence to show when the user declined the wallet prompt — or `null`
 * when the failure was something else, so a caller keeps its own copy for the
 * cases it already words well:
 *
 *   toast(userRejectionMessage(err, "approval") || "Approval failed. Try again.");
 *
 * `action` names what the user was doing, not the contract call: "vote",
 * "approval", "transfer".
 */
export function userRejectionMessage(error, action = "transaction") {
  return isUserRejection(error) ? `You rejected the ${action} in your wallet.` : null;
}

/**
 * The RPC refused the transaction because its gas limit was over the node's cap.
 *
 * Worth naming on its own because of how it LOOKS. A wallet that cannot
 * estimate gas — which is what happens when a call would always revert — falls
 * back to a huge limit, and Infura rejects anything above 16,777,216 outright.
 * viem then wraps that RPC refusal in a `ContractFunctionRevertedError`, so the
 * message reads "the contract function openAppeal reverted with the following
 * reason: ... transaction gas limit too high". It names the wrong cause, hides
 * the real revert, and sends whoever reads it hunting through the contract.
 *
 * The transaction never left the node, so nothing was spent.
 */
export function isGasLimitCapRejection(err) {
  if (!err) return false;
  const msg = `${err.shortMessage || ""} ${err.message || ""} ${
    err.details || ""
  }`.toLowerCase();
  return (
    msg.includes("gas limit too high") ||
    (msg.includes("gas limit") && msg.includes("cap:"))
  );
}

/** What to tell a user when the router is paused (midl-server plan §13.9 F1). */
export const ROUTER_PAUSED_MESSAGE =
  "The platform is temporarily paused. Please try again later.";

/** keccak256("RouterPaused()")[0:4]. */
const ROUTER_PAUSED_SELECTOR = "0xf562b5a0";

/**
 * The router refused because it is paused (`RouterPaused()`). The error name
 * sits in the message text viem builds from the ABI, which is what survives the
 * journal's serialization; a live error also carries it on its cause.
 */
export function isRouterPaused(err) {
  if (!err) return false;
  if (err.errorName === "RouterPaused") return true;
  if (err.cause?.data?.errorName === "RouterPaused") return true;
  const msg = `${err.shortMessage || ""} ${err.message || ""} ${err.details || ""}`;
  // Screens that call with a minimal ABI (no error entries) get only the
  // selector: "reverted with the following signature: 0xf562b5a0".
  return msg.includes("RouterPaused") || msg.toLowerCase().includes(ROUTER_PAUSED_SELECTOR);
}

/**
 * One sentence for a failed write, or null when the caller's own copy is
 * better. Use it in place of a bare `userRejectionMessage(...) || "X failed"`.
 */
export function writeFailureMessage(error, action = "transaction") {
  const rejection = userRejectionMessage(error, action);
  if (rejection) return rejection;
  if (isRouterPaused(error)) return ROUTER_PAUSED_MESSAGE;
  if (isGasLimitCapRejection(error)) {
    return (
      `Your wallet could not price this ${action}, which almost always means it ` +
      `would fail on-chain. It was never sent and nothing has been charged.`
    );
  }
  return null;
}
